// src/hooks/useSocket.ts
import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { getWebSocketToken } from "../api/api";
import { useAuth } from "../context/AuthContext";

const MAX_RETRIES = 5;
const baseURL =
    process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_API_PROD_BASE_URL
        : process.env.REACT_APP_API_LOCAL_BASE_URL;

export function useSocket() {
    const { user } = useAuth();
    const socketRef = useRef<Socket | null>(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        if (!user) return;

        let attempt = 0;
        let cancelled = false;

        const connectWithRetry = async () => {
            if (attempt >= MAX_RETRIES || cancelled) return;

            try {
                const token = await getWebSocketToken();
                if (cancelled) return;

                const socket = io(baseURL, {
                    auth: { token },
                    transports: ["websocket"],
                });

                socket.on("connect", () => {
                    setConnected(true);
                    console.log("✅ Global socket connected");
                });

                socket.on("disconnect", (reason) => {
                    setConnected(false);
                    console.warn("🔌 Socket disconnected:", reason);
                    if (reason !== "io client disconnect" && !cancelled) {
                        const delay = Math.pow(2, attempt) * 1000;
                        setTimeout(connectWithRetry, delay);
                        attempt++;
                    }
                });

                socketRef.current = socket;
            } catch (err) {
                console.error("❌ Socket token fetch failed:", err);
                if (!cancelled) {
                    const delay = Math.pow(2, attempt) * 1000;
                    setTimeout(connectWithRetry, delay);
                    attempt++;
                }
            }
        };

        connectWithRetry();

        return () => {
            cancelled = true;
            socketRef.current?.disconnect();
        };
    }, [user]);

    return {
        socket: socketRef.current,
        connected,
    };
}
